import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Copy, Check, ShieldCheck, KeyRound, Radio, RefreshCw, FileKey2, ExternalLink, Download } from "lucide-react";

const Step = ({ n, title, done, children }) => (
  <div className="relative pl-11 pb-6 last:pb-0">
    <div className="absolute left-0 top-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2"
      style={{ background: done ? "#16a34a" : "#fff", color: done ? "#fff" : "#334155", borderColor: done ? "#16a34a" : "#cbd5e1" }}>
      {done ? <Check size={14} /> : n}
    </div>
    <div className="absolute left-[13px] top-7 bottom-0 w-px bg-slate-200 last:hidden" />
    <div>
      <h4 className="text-sm font-semibold text-slate-800 mb-2">{title}</h4>
      {children}
    </div>
  </div>
);

export default function ZooplaPanel() {
  const [cfg, setCfg] = useState(null);
  const [csr, setCsr] = useState("");
  const [cert, setCert] = useState("");
  const [branch, setBranch] = useState("");
  const [busy, setBusy] = useState("");
  const [copied, setCopied] = useState(false);
  const [subj, setSubj] = useState({ org_name: "", locality: "", state: "", email: "" });

  const load = () => api.get("/zoopla/config").then(({ data }) => {
    setCfg(data); setBranch(data.branch_reference || ""); setCsr(data.csr_pem || "");
    const d = data.defaults || {};
    setSubj((s) => ({
      org_name: s.org_name || d.org_name || "",
      locality: s.locality || d.locality || "",
      state: s.state || d.state || "",
      email: s.email || d.email || "",
    }));
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const generateCsr = async () => {
    if (!subj.org_name.trim()) { toast.error("Enter your organisation name first"); return; }
    setBusy("csr");
    try {
      const { data } = await api.post("/zoopla/generate-csr", {
        org_name: subj.org_name.trim(), locality: subj.locality.trim(),
        state: subj.state.trim(), email: subj.email.trim(),
      });
      setCsr(data.csr_pem);
      toast.success("New CSR generated — submit it to Zoopla");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not generate CSR"); }
    finally { setBusy(""); }
  };

  const copyCsr = async () => {
    try {
      await navigator.clipboard.writeText(csr);
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    } catch { toast.error("Couldn't copy — use the Download .csr file button instead"); }
  };

  const downloadCsr = () => {
    const blob = new Blob([csr.endsWith("\n") ? csr : csr + "\n"], { type: "application/pkcs10" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = ((subj.org_name || "certificate").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "certificate") + ".csr";
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const saveCert = async () => {
    if (!cert.trim()) return;
    setBusy("cert");
    try {
      await api.post("/zoopla/certificate", { signed_cert_pem: cert.trim() });
      toast.success("Certificate installed");
      setCert(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Invalid certificate"); }
    finally { setBusy(""); }
  };

  const saveSettings = async (env) => {
    setBusy("settings");
    try {
      const { data } = await api.patch("/zoopla/config", { branch_reference: branch, environment: env || cfg.environment });
      setCfg(data);
      toast.success("Zoopla settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setBusy(""); }
  };

  const pushBranch = async () => {
    setBusy("branch");
    try {
      const { data } = await api.post("/zoopla/branch");
      if (data.result?.ok) toast.success("Branch pushed to Zoopla");
      else toast.error(`Zoopla returned ${data.result?.status_code}: ${JSON.stringify(data.result?.body)?.slice(0, 120)}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not push branch"); }
    finally { setBusy(""); }
  };

  if (!cfg) return <div className="p-6 flex justify-center"><Loader2 className="animate-spin text-slate-400" /></div>;

  return (
    <div data-testid="zoopla-panel">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
        <div>
          <h3 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2"><Radio size={18} className="text-blue-600" /> Zoopla Real-Time Listings</h3>
          <p className="text-sm text-slate-500">Connect your Zoopla account to publish listings automatically.</p>
        </div>
        <div className="flex items-center gap-2">
          {cfg.ready
            ? <span data-testid="zoopla-status-ready" className="text-xs font-semibold px-2.5 py-1 rounded-full bg-green-100 text-green-700 flex items-center gap-1"><ShieldCheck size={13} /> Connected ({cfg.environment})</span>
            : <span data-testid="zoopla-status-setup" className="text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-100 text-amber-700">Setup in progress</span>}
          <button onClick={load} className="text-slate-400 hover:text-slate-700" title="Refresh"><RefreshCw size={15} /></button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <Step n={1} title="Generate your certificate signing request (CSR)" done={cfg.has_csr}>
          <p className="text-sm text-slate-500 mb-3">We create a private key (kept securely on the server) and a CSR for <b>{subj.org_name || "your agency"}</b>. Check the details below, then generate — submit the CSR on Zoopla's CSR page.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <label className="block">
              <span className="block text-xs text-slate-500 mb-1">Organisation name</span>
              <input data-testid="zoopla-csr-org" value={subj.org_name} onChange={(e) => setSubj({ ...subj, org_name: e.target.value })}
                placeholder="e.g. Isco Estates Ltd" className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
            </label>
            <label className="block">
              <span className="block text-xs text-slate-500 mb-1">Email</span>
              <input data-testid="zoopla-csr-email" value={subj.email} onChange={(e) => setSubj({ ...subj, email: e.target.value })}
                placeholder="e.g. info@youragency.co.uk" className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
            </label>
            <label className="block">
              <span className="block text-xs text-slate-500 mb-1">Town / City</span>
              <input data-testid="zoopla-csr-locality" value={subj.locality} onChange={(e) => setSubj({ ...subj, locality: e.target.value })}
                placeholder="e.g. Salford" className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
            </label>
            <label className="block">
              <span className="block text-xs text-slate-500 mb-1">County</span>
              <input data-testid="zoopla-csr-state" value={subj.state} onChange={(e) => setSubj({ ...subj, state: e.target.value })}
                placeholder="e.g. Greater Manchester" className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
            </label>
          </div>
          <button data-testid="zoopla-generate-csr" onClick={generateCsr} disabled={busy === "csr"}
            className="bg-blue-700 text-white hover:bg-blue-800 rounded-lg px-4 py-2 text-sm font-medium flex items-center gap-2 disabled:opacity-60">
            {busy === "csr" ? <Loader2 size={15} className="animate-spin" /> : <KeyRound size={15} />} {cfg.has_csr ? "Regenerate CSR" : "Generate CSR"}
          </button>
          {cfg.has_csr && <p className="text-[11px] text-amber-600 mt-2">Regenerating creates a brand-new key & CSR and clears any installed certificate — you'll need Zoopla to sign the new one.</p>}
          {csr && (
            <div className="mt-3">
              <div className="relative">
                <textarea data-testid="zoopla-csr-output" readOnly value={csr} rows={5}
                  className="w-full text-[11px] font-mono bg-slate-50 border border-slate-200 rounded-lg p-3 resize-none" />
                <button data-testid="zoopla-copy-csr" onClick={copyCsr} className="absolute top-2 right-2 bg-white border border-slate-200 rounded-md px-2 py-1 text-xs flex items-center gap-1 hover:bg-slate-50">
                  {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
                </button>
              </div>
              <div className="flex items-center justify-between mt-2 flex-wrap gap-2">
                <button data-testid="zoopla-download-csr" onClick={downloadCsr}
                  className="bg-slate-800 text-white hover:bg-slate-900 rounded-lg px-3 py-1.5 text-xs font-medium flex items-center gap-1.5">
                  <Download size={13} /> Download .csr file
                </button>
                <a href="https://www.zoopla.co.uk/realtime-documentation/" target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1">
                  Zoopla docs — Section 3, Security &amp; authentication <ExternalLink size={11} />
                </a>
              </div>
              <p className="text-[11px] text-slate-500 mt-2">Zoopla require the <b>raw .csr text file</b> — upload this file (or paste the text above), do not convert it to PDF.</p>
            </div>
          )}
        </Step>

        <Step n={2} title="Install the certificate Zoopla signs for you" done={cfg.has_signed_cert}>
          <p className="text-sm text-slate-500 mb-3">After Zoopla verify your token (by phone) they'll return a signed certificate. Paste the full PEM below.</p>
          {cfg.cert_summary && (
            <div className="text-xs bg-green-50 border border-green-200 text-green-800 rounded-lg p-3 mb-3" data-testid="zoopla-cert-summary">
              <b>Installed.</b> Issued by {cfg.cert_summary.issuer || "Zoopla"} · expires {String(cfg.cert_summary.expires).slice(0, 10)}
            </div>
          )}
          <textarea data-testid="zoopla-cert-input" value={cert} onChange={(e) => setCert(e.target.value)} rows={4} placeholder="-----BEGIN CERTIFICATE-----"
            className="w-full text-[11px] font-mono bg-slate-50 border border-slate-200 rounded-lg p-3 resize-none focus:outline-none focus:ring-2 focus:ring-blue-600" />
          <button data-testid="zoopla-save-cert" onClick={saveCert} disabled={busy === "cert" || !cert.trim()}
            className="mt-2 bg-blue-700 text-white hover:bg-blue-800 rounded-lg px-4 py-2 text-sm font-medium flex items-center gap-2 disabled:opacity-50">
            {busy === "cert" ? <Loader2 size={15} className="animate-spin" /> : <FileKey2 size={15} />} Install certificate
          </button>
        </Step>

        <Step n={3} title="Branch reference & environment" done={!!cfg.branch_reference}>
          <p className="text-sm text-slate-500 mb-3">Your branch reference identifies this office to Zoopla (used on every listing).</p>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs text-slate-500 mb-1">Branch reference</label>
              <input data-testid="zoopla-branch-ref" value={branch} onChange={(e) => setBranch(e.target.value)} placeholder="e.g. ISCO-SALFORD-01"
                className="rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Environment</label>
              <select data-testid="zoopla-environment" value={cfg.environment} onChange={(e) => saveSettings(e.target.value)}
                className="rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600">
                <option value="sandbox">Sandbox (testing)</option>
                <option value="live">Live</option>
              </select>
            </div>
            <button data-testid="zoopla-save-branch" onClick={() => saveSettings()} disabled={busy === "settings"}
              className="h-[38px] bg-slate-800 text-white hover:bg-slate-900 rounded-lg px-4 text-sm font-medium flex items-center gap-2 disabled:opacity-60">
              {busy === "settings" ? <Loader2 size={15} className="animate-spin" /> : null} Save
            </button>
          </div>
        </Step>

        <Step n={4} title="Register your branch with Zoopla" done={false}>
          <p className="text-sm text-slate-500 mb-3">Once your certificate is installed, push your branch details to Zoopla. Then publish individual listings from the Lettings &amp; Sales pages.</p>
          <button data-testid="zoopla-push-branch" onClick={pushBranch} disabled={busy === "branch" || !cfg.ready}
            className="bg-blue-700 text-white hover:bg-blue-800 rounded-lg px-4 py-2 text-sm font-medium flex items-center gap-2 disabled:opacity-50">
            {busy === "branch" ? <Loader2 size={15} className="animate-spin" /> : <Radio size={15} />} Push branch to Zoopla
          </button>
          {!cfg.ready && <p className="text-xs text-amber-600 mt-2">Complete steps 1–3 (and install the signed certificate) to enable this.</p>}
        </Step>
      </div>
    </div>
  );
}
